import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  Autocomplete,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { API_ENDPOINT } from "../../../../../constants/constants";
import { getDbHeader } from "../../../../../utils2024/utils";

const CategorySelectorAccordions = ({ title = "", onSelectionChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Fetch categories with cleanup (unchanged)
  useEffect(() => {
    const abortController = new AbortController();
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_ENDPOINT}/event/get-product-categories`,
          {
            ...getDbHeader(),
            signal: abortController.signal,
          }
        );
        setData(response.data);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error("Fetch error:", err);
        setError("Failed to load categories");
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => abortController.abort();
  }, []);

  // Check if a category is selected (unchanged)
  const isCategorySelected = useCallback(
    (category) => {
      return selectedCategories.some(
        (sel) =>
          sel.MainCategory === category.MainCategory &&
          (sel.Category ?? null) === (category.Category ?? null) &&
          (sel.SubCategory ?? null) === (category.SubCategory ?? null)
      );
    },
    [selectedCategories]
  );

  // Toggle single checkbox (used by double/triple view)
  const handleCheckboxChange = useCallback(
    (category) => {
      setSelectedCategories((prev) => {
        const exists = prev.some(
          (sel) =>
            sel.MainCategory === category.MainCategory &&
            (sel.Category ?? null) === (category.Category ?? null) &&
            (sel.SubCategory ?? null) === (category.SubCategory ?? null)
        );

        const newSelections = exists
          ? prev.filter(
              (sel) =>
                !(
                  sel.MainCategory === category.MainCategory &&
                  (sel.Category ?? null) === (category.Category ?? null) &&
                  (sel.SubCategory ?? null) === (category.SubCategory ?? null)
                )
            )
          : [...prev, category];
        onSelectionChange?.(newSelections);
        return newSelections;
      });
    },
    [onSelectionChange]
  );

  // Memoized grouped data (unchanged)
  const { mainGroups, categoryGroups } = useMemo(() => {
    if (!data?.categories) return { mainGroups: {}, categoryGroups: {} };

    const groups = data.categories.reduce((acc, cat) => {
      const main = cat.MainCategory || "Unknown";
      if (!acc[main]) acc[main] = [];
      acc[main].push(cat);
      return acc;
    }, {});

    const catGroups =
      data.type === "triple"
        ? data.categories.reduce((acc, item) => {
            const key = `${item.MainCategory}-${item.Category}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
          }, {})
        : {};

    return { mainGroups: groups, categoryGroups: catGroups };
  }, [data]);

  // ----- Precompute options for the "single" Autocomplete -----
  const singleOptions = useMemo(() => {
    if (!data?.categories || data.type !== "single") return [];
    return Object.keys(mainGroups).map((main) => ({
      MainCategory: main,
      Category: null,
      SubCategory: null,
    }));
  }, [data, mainGroups]);

  // Render helpers (single render removed, double/triple unchanged)
  const renderDouble = (items) =>
    items.map((cat, idx) => (
      <FormControlLabel
        sx={{ width: "100%" }}
        key={`${cat.MainCategory}-${cat.Category}-${idx}`}
        control={
          <Checkbox
            checked={isCategorySelected(cat)}
            onChange={() => handleCheckboxChange(cat)}
          />
        }
        label={<Typography variant="body2">{cat.Category || "N/A"}</Typography>}
      />
    ));

  const renderTriple = (mainCategory) =>
    Object.entries(categoryGroups)
      .filter(([key]) => key.startsWith(mainCategory))
      .map(([key, items]) => (
        <Box key={key} mb={2}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            {key.split("-")[1]}
          </Typography>
          {items.map((subItem, subIdx) => (
            <FormControlLabel
              sx={{ width: "100%" }}
              key={`${key}-${subIdx}`}
              control={
                <Checkbox
                  checked={isCategorySelected(subItem)}
                  onChange={() => handleCheckboxChange(subItem)}
                />
              }
              label={
                <Typography variant="body2">
                  {subItem.SubCategory || "N/A"}
                </Typography>
              }
            />
          ))}
        </Box>
      ));

  // Loading / error states (unchanged)
  if (loading)
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data?.categories?.length)
    return <Alert severity="info">No categories available</Alert>;

  // ----- Conditional rendering based on data.type -----
  return (
    <Box my={2}>
      {title && (
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
      )}

      {data.type === "single" ? (
        /* ---------- Multi‑select Autocomplete for SINGLE ---------- */
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={singleOptions}
          value={selectedCategories.filter(
            (sel) => sel.Category === null && sel.SubCategory === null
          )}
          getOptionLabel={(option) => option.MainCategory}
          isOptionEqualToValue={(option, value) =>
            option.MainCategory === value.MainCategory
          }
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} style={{ marginRight: 8 }} />
              {option.MainCategory}
            </li>
          )}
          onChange={(event, newValue) => {
            setSelectedCategories(newValue);
            onSelectionChange?.(newValue);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Select Main Categories" />
          )}
        />
      ) : (
        /* ---------- Accordion layout for DOUBLE / TRIPLE ---------- */
        Object.entries(mainGroups).map(([mainCategory, items]) => (
          <Accordion key={`${mainCategory}-${data.type}`}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="bold">
                {mainCategory}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {data.type === "double" && renderDouble(items)}
              {data.type === "triple" && renderTriple(mainCategory)}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default React.memo(CategorySelectorAccordions);
